import { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useObservable } from '@legendapp/state/react';
import { user$, setCurrentBranch } from '../store/userStore';
import { Branch } from '@/types/Branch.interface';
import { Role } from '@/enums/role';
import { useTranslation } from 'react-i18next';
import { branchService } from '@/data/branchService';
import { authService } from '@/data/authService';
import { AUTH_BRANCH_RESET_EVENT } from '@/data/authSession';
import { useLocation } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { matchesQuery } from '@/utils/textSearch';

/** With more branches than this the list gets a search field. */
const SEARCH_THRESHOLD = 6;

const STORAGE_KEYS = {
  SELECTED_BRANCH_ID: 'selected_branch_id',
  VIEW_MODE_PREFERENCE: 'view_mode_preference',
  LAST_BRANCH_ID: 'last_branch_id'
};

interface SwitchBranchResponse {
  accessToken: string;
  refreshToken: string;
}

interface BranchWithPrimary extends Branch {
  isPrimary: boolean;
}

const BranchSelector = () => {
  const { t } = useTranslation();
  const [branches, setBranches] = useState<BranchWithPrimary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBranchId, setSelectedBranchId] = useState<number | ''>('');
  const [query, setQuery] = useState('');
  const user = useObservable(user$);
  const location = useLocation();

  const isOnPOSPage = location.pathname.startsWith('/pos');
  const userRole = user.get().role;

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoading(true);
        let response;

        if (userRole === Role.SUPER_ADMIN) {
          const data = await branchService.getAllFlat({
            status: 'ACTIVE',
            getAll: true
          });
          if (data) {
            const branchesWithPrimary: BranchWithPrimary[] = data.map(
              (branch) => ({
                ...branch,
                isPrimary: false
              })
            );
            setBranches(branchesWithPrimary);
            response = { data: branchesWithPrimary };
          }
        } else if (userRole === Role.COMPANY_ADMIN) {
          const companyId = user.get().company?.id;
          if (companyId) {
            const data = await branchService.getAllFlat({
              companyId: companyId,
              status: 'ACTIVE',
              getAll: true
            });
            if (data) {
              const branchesWithPrimary: BranchWithPrimary[] = data.map(
                (branch) => ({
                  ...branch,
                  isPrimary: false
                })
              );
              setBranches(branchesWithPrimary);
              response = { data: branchesWithPrimary };
            }
          }
        } else {
          response = { data: await branchService.getMyBranches() };
        }

        if (response?.data && response.data.length > 0) {
          setBranches(response.data);

          const viewModePreference = localStorage.getItem(
            STORAGE_KEYS.VIEW_MODE_PREFERENCE
          );
          const storedBranchId = localStorage.getItem(
            STORAGE_KEYS.SELECTED_BRANCH_ID
          );

          if (
            viewModePreference === 'admin' &&
            (userRole === Role.COMPANY_ADMIN || userRole === Role.SUPER_ADMIN)
          ) {
            setSelectedBranchId('');
            setCurrentBranch(undefined as any);
          } else if (
            storedBranchId &&
            response.data.some((b) => b.id === parseInt(storedBranchId))
          ) {
            setSelectedBranchId(parseInt(storedBranchId));

            const selectedBranch = response.data.find(
              (b) => b.id === parseInt(storedBranchId)
            );
            if (selectedBranch) {
              setCurrentBranch(selectedBranch);
            }
          } else {
            const currentBranchId = user.get().currentBranch?.id;

            if (
              currentBranchId &&
              response.data.some((b) => b.id === currentBranchId)
            ) {
              setSelectedBranchId(currentBranchId);
              localStorage.setItem(
                STORAGE_KEYS.SELECTED_BRANCH_ID,
                currentBranchId.toString()
              );
            } else {
              if (
                userRole === Role.COMPANY_ADMIN ||
                userRole === Role.SUPER_ADMIN
              ) {
                setSelectedBranchId('');
                setCurrentBranch(undefined as any);
                localStorage.setItem(
                  STORAGE_KEYS.VIEW_MODE_PREFERENCE,
                  'admin'
                );
              } else {
                const primaryBranch = response.data.find((b) => b.isPrimary);
                if (primaryBranch) {
                  setSelectedBranchId(primaryBranch.id);
                  localStorage.setItem(
                    STORAGE_KEYS.SELECTED_BRANCH_ID,
                    primaryBranch.id.toString()
                  );
                } else {
                  setSelectedBranchId(response.data[0].id);
                  localStorage.setItem(
                    STORAGE_KEYS.SELECTED_BRANCH_ID,
                    response.data[0].id.toString()
                  );
                }
              }
            }
          }
        } else {
          setBranches([]);
        }
      } catch (error) {
        console.error('Error fetching user branches:', error);
        setBranches([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBranches();
  }, [isOnPOSPage, userRole, user]);

  const switchBranch = async (
    branchId: number
  ): Promise<SwitchBranchResponse> => {
    try {
      return await authService.switchBranch(branchId);
    } catch (error) {
      console.error('Error switching branch:', error);
      throw error;
    }
  };

  const handleBranchChange = async (branchId: number | 'admin') => {
    try {
      setLoading(true);

      if (branchId === 'admin') {
        // authService.switchBranch yeni anahtarları oturum kilidi içinde saklar.
        await authService.switchBranch(null);

        setSelectedBranchId('');

        if (selectedBranchId) {
          localStorage.setItem(
            STORAGE_KEYS.LAST_BRANCH_ID,
            selectedBranchId.toString()
          );
        }

        localStorage.removeItem(STORAGE_KEYS.SELECTED_BRANCH_ID);
        localStorage.setItem(STORAGE_KEYS.VIEW_MODE_PREFERENCE, 'admin');

        setCurrentBranch(undefined as any);

        window.dispatchEvent(new Event('ability-update'));

        window.location.reload();
        return;
      }

      await switchBranch(branchId);

      setSelectedBranchId(branchId);

      localStorage.setItem(
        STORAGE_KEYS.SELECTED_BRANCH_ID,
        branchId.toString()
      );
      localStorage.setItem(STORAGE_KEYS.LAST_BRANCH_ID, branchId.toString());
      localStorage.setItem(STORAGE_KEYS.VIEW_MODE_PREFERENCE, 'branch');

      const selectedBranch = branches.find((b) => b.id === branchId);

      if (selectedBranch) {
        await setCurrentBranch(selectedBranch);
      }

      window.dispatchEvent(new Event('ability-update'));

      // If we're on a specific branch's management/detail page, follow the
      // switch there too — otherwise the header shows the new branch while
      // the page underneath keeps editing the old one (silently mis-filing
      // whatever gets saved next, e.g. a POS provider assigned to the wrong
      // branch).
      const branchDetailMatch = location.pathname.match(
        /^\/management\/branches\/\d+/
      );
      if (branchDetailMatch) {
        window.location.href = `/management/branches/${branchId}`;
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error switching branch:', error);
      setLoading(false);
    }
  };

  // Sunucu yenileme sonrası seçili şubeye dönüşü reddederse seçim anahtarın
  // şubesine çekilir (localStorage authSession tarafından güncellenir).
  useEffect(() => {
    const onBranchReset = (event: Event) => {
      const { branchId } = (event as CustomEvent<{ branchId: number | null }>)
        .detail;
      setSelectedBranchId(branchId ?? '');
      setCurrentBranch(
        (branches.find((b) => b.id === branchId) ?? undefined) as any
      );
    };
    window.addEventListener(AUTH_BRANCH_RESET_EVENT, onBranchReset);
    return () =>
      window.removeEventListener(AUTH_BRANCH_RESET_EVENT, onBranchReset);
  }, [branches]);

  useEffect(() => {
    if (selectedBranchId && branches.length > 0) {
      const branchExists = branches.some((b) => b.id === selectedBranchId);
      if (!branchExists) {
        setSelectedBranchId('');
      }
    }
  }, [selectedBranchId, branches]);

  const searchable = branches.length > SEARCH_THRESHOLD;
  // The selected branch stays in the list even when filtered out, so the
  // Select always has an option for its value.
  const shownBranches = searchable
    ? branches.filter(
        (branch) =>
          branch.id === selectedBranchId ||
          matchesQuery(`${branch.name} ${branch.company?.name ?? ''}`, query)
      )
    : branches;

  return (
    <Box
      sx={{
        minWidth: { xs: 0, sm: 180, lg: 200 },
        width: { xs: 118, sm: 'auto' },
        maxWidth: { xs: 118, sm: 250, lg: 300, xl: 350 },
        mx: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5
      }}
    >
      <FormControl fullWidth size="small">
        <Select
          value={selectedBranchId}
          onChange={(e) =>
            handleBranchChange(e.target.value as number | 'admin')
          }
          displayEmpty
          disabled={loading}
          onClose={() => setQuery('')}
          MenuProps={{
            autoFocus: !searchable,
            PaperProps: { sx: { maxHeight: 420 } }
          }}
          sx={{
            backgroundColor: 'background.paper',
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center'
            }
          }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminPanelSettingsIcon fontSize="small" color="primary" />
                  <Typography variant="body2" fontWeight="medium">
                    {userRole === Role.COMPANY_ADMIN ||
                    userRole === Role.SUPER_ADMIN
                      ? t('admin.view')
                      : t('select.branch')}
                  </Typography>
                </Box>
              );
            }

            const branch = branches.find((b) => b.id === selected);

            return (
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {branch ? branch.name : t('select.branch')}
              </Typography>
            );
          }}
        >
          {searchable && (
            <ListSubheader sx={{ pt: 1, pb: 1 }}>
              <TextField
                size="small"
                autoFocus
                fullWidth
                value={query}
                placeholder={t('branch.search.placeholder')}
                inputProps={{ 'aria-label': t('branch.search.placeholder') }}
                onChange={(event) => setQuery(event.target.value)}
                // Keep typing in the field: the menu would otherwise jump to
                // the item starting with the typed letter.
                onKeyDown={(event) => {
                  if (event.key !== 'Escape') event.stopPropagation();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </ListSubheader>
          )}
          {(userRole === Role.COMPANY_ADMIN ||
            userRole === Role.SUPER_ADMIN) && (
            <MenuItem value="admin">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AdminPanelSettingsIcon fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {t('admin.view')}
                </Typography>
              </Box>
            </MenuItem>
          )}

          {searchable && query.trim() && shownBranches.length === 0 && (
            <MenuItem value="" disabled>
              <Typography variant="body2">
                {t('branch.search.empty')}
              </Typography>
            </MenuItem>
          )}
          {branches.length > 0 ? (
            shownBranches.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                <Box>
                  <Typography variant="body2">
                    {branch.name} {branch.isPrimary && t('branch.primary')}
                  </Typography>
                  {searchable && branch.company?.name && (
                    <Typography variant="caption" color="text.secondary">
                      {branch.company.name}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem value="" disabled>
              <Typography variant="body2">
                {t('no.branches.available')}
              </Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default BranchSelector;
